import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Atom, Plus, Minus, RotateCcw, Check, ChevronRight } from 'lucide-react';

const ELEMENTS = [
  { num: 1, sym: 'H', name: 'Hydrogen', p: 1, n: 0, e: 1, shells: [1], fact: 'Most abundant element in the universe!', color: '#FF6B6B' },
  { num: 2, sym: 'He', name: 'Helium', p: 2, n: 2, e: 2, shells: [2], fact: 'Makes balloons float and your voice squeaky!', color: '#FFC03D' },
  { num: 3, sym: 'Li', name: 'Lithium', p: 3, n: 4, e: 3, shells: [2,1], fact: 'Lightest metal! Used in phone batteries.', color: '#4D96FF' },
  { num: 6, sym: 'C', name: 'Carbon', p: 6, n: 6, e: 6, shells: [2,4], fact: 'Element of life! Diamonds are pure carbon.', color: '#64748B' },
  { num: 7, sym: 'N', name: 'Nitrogen', p: 7, n: 7, e: 7, shells: [2,5], fact: '78% of air you breathe is nitrogen!', color: '#4D96FF' },
  { num: 8, sym: 'O', name: 'Oxygen', p: 8, n: 8, e: 8, shells: [2,6], fact: 'Essential for breathing and fire!', color: '#FF6B6B' },
  { num: 10, sym: 'Ne', name: 'Neon', p: 10, n: 10, e: 10, shells: [2,8], fact: 'Makes bright glowing signs!', color: '#FFC03D' },
  { num: 11, sym: 'Na', name: 'Sodium', p: 11, n: 12, e: 11, shells: [2,8,1], fact: 'Combined with chlorine = table salt!', color: '#A493FF' },
  { num: 17, sym: 'Cl', name: 'Chlorine', p: 17, n: 18, e: 17, shells: [2,8,7], fact: 'Keeps swimming pool water clean!', color: '#38E54D' },
  { num: 20, sym: 'Ca', name: 'Calcium', p: 20, n: 20, e: 20, shells: [2,8,8,2], fact: 'Makes your bones and teeth strong!', color: '#FF6B6B' },
];

const AtomBuilder = () => {
  const navigate = useNavigate();
  const [levelIdx, setLevelIdx] = useState(0);
  const [protons, setProtons] = useState(0);
  const [neutrons, setNeutrons] = useState(0);
  const [electrons, setElectrons] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [showFact, setShowFact] = useState(false);

  const el = ELEMENTS[levelIdx];

  const reset = () => { setProtons(0); setNeutrons(0); setElectrons(0); setFeedback(null); setShowFact(false); };

  const checkAtom = () => {
    if (protons === el.p && neutrons === el.n && electrons === el.e) {
      setScore(s => s + 20);
      setFeedback({ correct: true, msg: `You built ${el.name}! Amazing!` });
      setShowFact(true);
    } else {
      const hints = [];
      if (protons !== el.p) hints.push(`Protons: need ${el.p}`);
      if (neutrons !== el.n) hints.push(`Neutrons: need ${el.n}`);
      if (electrons !== el.e) hints.push(`Electrons: need ${el.e}`);
      setFeedback({ correct: false, msg: `Almost! ${hints.join(', ')}` });
    }
  };

  const nextLevel = () => {
    if (levelIdx < ELEMENTS.length - 1) { setLevelIdx(i => i+1); reset(); }
    else setCompleted(true);
  };

  const renderAtom = () => {
    const shellRadii = [35, 60, 85, 110];
    return (
      <svg viewBox="-130 -130 260 260" className="w-64 h-64 mx-auto">
        {/* Electron shells */}
        {el.shells.map((_, i) => (
          <circle key={i} cx="0" cy="0" r={shellRadii[i]} fill="none" stroke="#E2E8F0" strokeWidth="1.5" strokeDasharray="4 3" />
        ))}
        {/* Nucleus */}
        <circle cx="0" cy="0" r="22" fill={el.color} opacity="0.3" />
        {/* Protons in nucleus */}
        {Array.from({length: Math.min(protons, 20)}, (_, i) => {
          const angle = (i / Math.max(protons, 1)) * Math.PI * 2;
          const r = protons <= 1 ? 0 : 10;
          return <circle key={`p${i}`} cx={Math.cos(angle)*r} cy={Math.sin(angle)*r} r="5" fill="#FF6B6B" stroke="#CC4444" strokeWidth="1" />;
        })}
        {/* Neutrons in nucleus */}
        {Array.from({length: Math.min(neutrons, 20)}, (_, i) => {
          const angle = (i / Math.max(neutrons, 1)) * Math.PI * 2 + 0.3;
          const r = neutrons <= 1 ? 5 : 12;
          return <circle key={`n${i}`} cx={Math.cos(angle)*r} cy={Math.sin(angle)*r} r="4" fill="#64748B" stroke="#475569" strokeWidth="1" />;
        })}
        {/* Electrons on shells */}
        {(() => {
          let placed = 0;
          const elems = [];
          el.shells.forEach((max, shellIdx) => {
            const toPlace = Math.min(electrons - placed, max);
            for (let j = 0; j < toPlace; j++) {
              const angle = (j / max) * Math.PI * 2 - Math.PI/2;
              elems.push(
                <circle key={`e${placed+j}`} cx={Math.cos(angle)*shellRadii[shellIdx]} cy={Math.sin(angle)*shellRadii[shellIdx]} r="5" fill="#4D96FF" stroke="#3B7BE0" strokeWidth="1">
                  <animate attributeName="opacity" values="1;0.5;1" dur="2s" repeatCount="indefinite" begin={`${j*0.2}s`} />
                </circle>
              );
            }
            placed += toPlace;
          });
          return elems;
        })()}
      </svg>
    );
  };

  if (completed) {
    return (
      <div className="min-h-screen bg-[#F4F8FB] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-10 shadow-2xl max-w-md w-full text-center border-2 border-slate-100">
          <div className="text-7xl mb-4"><Atom className="w-20 h-20 mx-auto text-pink-500" /></div>
          <h2 className="text-4xl font-black text-[#1E293B] mb-2">Atom Master!</h2>
          <p className="text-6xl font-black text-pink-500 mb-2">{score}</p>
          <p className="text-xl text-[#64748B] mb-8">out of {ELEMENTS.length * 20} points</p>
          <div className="space-y-3">
            <button onClick={() => { setLevelIdx(0); setScore(0); setCompleted(false); reset(); }} className="w-full py-4 bg-pink-500 text-white rounded-2xl font-bold border-b-4 border-pink-700 active:border-b-0 active:translate-y-1 transition-all">Play Again</button>
            <button onClick={() => navigate('/games')} className="w-full py-4 bg-slate-100 text-[#1E293B] rounded-2xl font-bold">Back to Brain Arena</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-rose-50" data-testid="atom-builder-game">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/games')} className="flex items-center gap-2 text-[#64748B] font-semibold"><ArrowLeft className="w-5 h-5" />Back</button>
          <div className="flex gap-3"><span className="bg-pink-500 text-white px-4 py-2 rounded-2xl font-bold">{levelIdx+1}/{ELEMENTS.length}</span><span className="bg-[#4D96FF] text-white px-4 py-2 rounded-2xl font-bold">{score}pts</span></div>
        </div>
        <div className="bg-white rounded-3xl p-8 border-2 border-slate-100 shadow-xl text-center">
          <h2 className="text-3xl font-black text-[#1E293B] mb-1">Build: <span style={{color: el.color}}>{el.name}</span></h2>
          <p className="text-lg text-[#64748B] mb-2">Atomic Number: {el.num} | Symbol: <span className="font-black text-2xl" style={{color: el.color}}>{el.sym}</span></p>

          {renderAtom()}

          <div className="grid grid-cols-3 gap-4 mt-6 mb-6">
            <ParticleControl label="Protons" value={protons} onChange={setProtons} color="bg-red-100 text-red-600" max={25} />
            <ParticleControl label="Neutrons" value={neutrons} onChange={setNeutrons} color="bg-slate-100 text-slate-600" max={30} />
            <ParticleControl label="Electrons" value={electrons} onChange={setElectrons} color="bg-blue-100 text-blue-600" max={25} />
          </div>

          {feedback && (
            <div className={`p-4 rounded-2xl font-bold text-lg mb-4 ${feedback.correct ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
              {feedback.msg}
            </div>
          )}
          {showFact && <div className="p-4 rounded-2xl bg-yellow-100 text-yellow-800 font-semibold mb-4">Fun Fact: {el.fact}</div>}

          <div className="flex gap-3 justify-center">
            <button onClick={reset} className="px-6 py-3 bg-slate-100 rounded-2xl font-bold text-[#64748B] flex items-center gap-2"><RotateCcw className="w-5 h-5" />Reset</button>
            {showFact ? (
              <button onClick={nextLevel} className="px-8 py-3 bg-pink-500 text-white rounded-2xl font-bold border-b-4 border-pink-700 active:border-b-0 active:translate-y-1 transition-all flex items-center gap-2" data-testid="next-element-btn"><ChevronRight className="w-5 h-5" />Next Element</button>
            ) : (
              <button onClick={checkAtom} className="px-8 py-3 bg-[#38E54D] text-white rounded-2xl font-bold border-b-4 border-green-600 active:border-b-0 active:translate-y-1 transition-all flex items-center gap-2" data-testid="check-atom-btn"><Check className="w-5 h-5" />Check Atom</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ParticleControl = ({ label, value, onChange, color, max }) => (
  <div className={`p-4 rounded-2xl ${color}`}>
    <p className="text-sm font-bold mb-2">{label}</p>
    <div className="flex items-center justify-center gap-2">
      <button onClick={() => onChange(Math.max(0, value-1))} className="w-10 h-10 bg-white rounded-xl flex items-center justify-center font-bold shadow active:scale-90 transition-all"><Minus className="w-4 h-4" /></button>
      <span className="text-3xl font-black w-12 text-center">{value}</span>
      <button onClick={() => onChange(Math.min(max, value+1))} className="w-10 h-10 bg-white rounded-xl flex items-center justify-center font-bold shadow active:scale-90 transition-all"><Plus className="w-4 h-4" /></button>
    </div>
  </div>
);

export default AtomBuilder;
